/**
 * /vaccines — Vaccination Research Explorer hub.
 *
 * Q1 (What is it?) — explains TCP's approach: official information +
 *                    structured evidence + clear epistemic boundaries.
 * Q2 (Connected?) — country selector + age-cohort entry points.
 * Q3 (Evidence?)  — links to evidence/safety section approach.
 * Q4 (Next?)      — list of every indexable vaccine.
 *
 * SEO-first: every link is crawlable; the hub itself is canonical-tagged
 * + JSON-LD Dataset so search engines treat the section as a single
 * cohesive knowledge resource.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Calendar, Syringe, ArrowRight, Shield, BookOpen, AlertCircle } from "lucide-react";
import api from "../../lib/api";
import SEO from "../../components/SEO";
import ResearchSearchBox from "../../components/research/ResearchSearchBox";
import { AGE_COHORTS, COUNTRIES } from "../../lib/vaccineCohorts";

export default function VaccinesHubPage() {
    const [vaccines, setVaccines] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/vaccines")
            .then(({ data }) => setVaccines(data || []))
            .catch(() => setVaccines([]))
            .finally(() => setLoading(false));
    }, []);

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Dataset",
        name: "Vaccination Research Explorer",
        description: "A structured, evidence-graded research index covering UK and US childhood vaccination schedules, with linked investigations, sources and evidence types.",
        creator: { "@type": "Organization", name: "The Context Project" },
        url: "https://library.thecontextproject.com/vaccines",
    };

    return (
        <div className="min-h-screen bg-[#0B1220] text-[#CBD5E1]" data-testid="vaccines-hub-page">
            <SEO
                title="Vaccination Research Explorer"
                description="Official UK and US childhood vaccination schedules — explored alongside published research, regulatory documents, safety surveillance data and TCP investigations."
                canonicalPath="/vaccines"
                jsonLd={jsonLd}
            />
            <div className="h-px bg-gradient-to-r from-transparent via-[#56CCF2]/70 to-transparent" />

            {/* Hero */}
            <header className="max-w-5xl mx-auto px-6 pt-16 pb-12">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#56CCF2] font-semibold mb-3">Research Explorer</div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-6">
                    Vaccination Research Explorer
                </h1>
                <p className="text-[17px] text-[#94A3B8] leading-relaxed max-w-3xl mb-8">
                    Official UK and US childhood vaccination schedules — explored alongside published research,
                    regulatory documents, safety surveillance data and TCP investigations. <span className="text-white">Context before conclusions.</span>
                </p>
                <div className="flex flex-wrap items-center gap-3 text-[12px] text-[#64748B]">
                    <span className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-[#56CCF2]" /> Evidence labelled by type</span>
                    <span className="text-[#475569]">·</span>
                    <span className="inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-[#56CCF2]" /> Linked to investigations</span>
                    <span className="text-[#475569]">·</span>
                    <span className="inline-flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5 text-[#56CCF2]" /> Surveillance signals clearly distinguished from causation</span>
                </div>
                <div className="mt-8" data-testid="hub-search">
                    <ResearchSearchBox autoFocus={false} />
                </div>
            </header>

            {/* Country selector */}
            <section className="max-w-5xl mx-auto px-6 pb-12">
                <h2 className="text-[10px] uppercase tracking-[0.28em] text-[#94A3B8] font-semibold mb-4">Choose a schedule</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {COUNTRIES.map((c) => (
                        <Link
                            key={c.code}
                            to={`/vaccines/${c.code.toLowerCase()}`}
                            data-testid={`hub-country-${c.code.toLowerCase()}`}
                            className="group bg-[#111827] border border-[#243044] hover:border-[#56CCF2]/60 rounded-xl p-6 transition-colors"
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <Globe className="w-5 h-5 text-[#56CCF2]" />
                                <span className="text-[11px] uppercase tracking-[0.22em] text-[#64748B]">{c.regulator}</span>
                            </div>
                            <h3 className="text-2xl font-bold text-white tracking-tight mb-1">{c.label}</h3>
                            <div className="text-[13px] text-[#94A3B8]">Routine childhood schedule</div>
                            <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#56CCF2] group-hover:text-white transition-colors">
                                Explore the schedule <ArrowRight className="w-3.5 h-3.5" />
                            </div>
                        </Link>
                    ))}
                </div>
                <div className="mt-4 flex justify-end">
                    <Link
                        to="/vaccines/compare/uk-vs-us"
                        data-testid="hub-compare"
                        className="text-xs text-[#56CCF2] hover:text-white transition-colors"
                    >
                        Or compare UK vs US side-by-side →
                    </Link>
                </div>
            </section>

            {/* Age cohorts */}
            <section className="max-w-5xl mx-auto px-6 pb-12">
                <h2 className="text-[10px] uppercase tracking-[0.28em] text-[#94A3B8] font-semibold mb-4">Or browse by age</h2>
                <div className="flex flex-wrap gap-2">
                    {AGE_COHORTS.map((c) => (
                        <Link
                            key={c.slug}
                            to={`/vaccines/by-age/${c.slug}`}
                            data-testid={`hub-cohort-${c.slug}`}
                            className="inline-flex items-center gap-2 bg-[#111827] border border-[#243044] hover:border-[#56CCF2]/60 hover:text-white text-[#CBD5E1] rounded-full px-4 py-2 text-sm transition-colors"
                        >
                            <Calendar className="w-3.5 h-3.5 text-[#56CCF2]" />
                            {c.label}
                        </Link>
                    ))}
                </div>
            </section>

            {/* Vaccine index */}
            <section className="max-w-5xl mx-auto px-6 pb-20">
                <h2 className="text-[10px] uppercase tracking-[0.28em] text-[#94A3B8] font-semibold mb-4">Indexed vaccines</h2>
                {loading ? (
                    <div className="text-[13px] text-[#64748B] py-6">Loading…</div>
                ) : vaccines.length === 0 ? (
                    <div className="text-[13px] text-[#64748B] py-6 italic">No vaccines indexed yet. Check back soon.</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {vaccines.map((v) => (
                            <Link
                                key={v.id}
                                to={`/vaccines/v/${v.slug}`}
                                data-testid={`hub-vaccine-${v.slug}`}
                                className="group bg-[#111827] border border-[#243044] hover:border-[#56CCF2]/50 rounded-lg p-4 transition-colors"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Syringe className="w-3.5 h-3.5 text-[#56CCF2]" />
                                    <span className="text-[10px] uppercase tracking-wider text-[#64748B]">Vaccine</span>
                                </div>
                                <h3 className="text-sm font-semibold text-white group-hover:text-[#56CCF2] transition-colors leading-snug">
                                    {v.display_name}
                                </h3>
                                {(v.aka || []).length > 0 ? (
                                    <div className="text-[11px] text-[#64748B] mt-1.5">aka {v.aka.join(", ")}</div>
                                ) : null}
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
